/**
 * Hook for player operations in canvas
 * Extracted from BaseCanvas.tsx to reduce component complexity
 */

import React, { useCallback } from 'react'
import { PLAYER_RADIUS_FEET, TOOL_ERASE, TOOL_FILL, TOOL_SELECT } from '@/constants'
import { DEFAULT_PLAYER_COLOR } from '@/constants/Formation.constants'
import { findDrawingSnapTarget } from '@/utils/Drawing.utils'
import { snapToGrid } from '@/utils/GridSnap.utils'
import type { ControlPoint, Drawing, DrawingState } from '@/types'
import type { FieldCoordinateSystem } from '@/utils'
import type { BaseCanvasCallbacks, Player } from '../BaseCanvas.types'

interface UsePlayerOperationsOptions {
	players: Player[]
	drawings: Drawing[]
	drawingState: DrawingState
	coordSystem: FieldCoordinateSystem
	callbacks: BaseCanvasCallbacks
	zoom: number
	panX: number
	panY: number
	onBeforeStateChange?: () => void
	onPlayerMoveIntercept?: (id: string, x: number, y: number, player: Player) => boolean | void
	onLinkDrawingToPlayer: (
		drawingId: string,
		pointId: string,
		playerId: string,
		newPlayerOverride?: Player,
	) => void
}

interface UsePlayerOperationsReturn {
	handleCanvasClickForPlayer: (
		e: React.MouseEvent<HTMLDivElement>,
		whiteboardRef: React.RefObject<HTMLDivElement | null>,
	) => Promise<Player | null>
	handlePlayerPositionChange: (id: string, newX: number, newY: number) => void
	handlePlayerLabelClick: (id: string, x: number, y: number) => { id: string; x: number; y: number } | null
	handlePlayerDelete: (id: string) => void
	handleFillPlayer: (id: string) => void
	isPlayerInteractable: boolean
}

/**
 * Manages player operations: add, move, delete, fill, and label interactions
 */
export function usePlayerOperations({
										players,
										drawings,
										drawingState,
										coordSystem,
										callbacks,
										zoom,
										panX,
										panY,
										onBeforeStateChange,
										onPlayerMoveIntercept,
										onLinkDrawingToPlayer,
									}: UsePlayerOperationsOptions): UsePlayerOperationsReturn {
	// Determine if players should be interactable based on current tool
	const isPlayerInteractable =
		drawingState.tool === TOOL_SELECT ||
		drawingState.tool === TOOL_FILL ||
		drawingState.tool === TOOL_ERASE

	const handleCanvasClickForPlayer = useCallback(
		async (
			e: React.MouseEvent<HTMLDivElement>,
			whiteboardRef: React.RefObject<HTMLDivElement | null>,
		): Promise<Player | null> => {
			if (!whiteboardRef.current) return null

			// Skip if clicking on a control point node - let the node's click handler add the player
			const target = e.target as Element
			if (target.tagName === 'circle' && target.getAttribute('r') === '6') {
				return null
			}

			const rect = whiteboardRef.current.getBoundingClientRect()
			const screenX = e.clientX - rect.left
			const screenY = e.clientY - rect.top

			// Use viewport-aware coordinate conversion
			const feetCoords = coordSystem.screenToFeet(screenX, screenY, zoom, panX, panY)

			// Apply grid snapping to initial placement
			const snappedCoords = snapToGrid(feetCoords.x, feetCoords.y)

			const newPlayer: Player = {
				id: crypto.randomUUID(),
				x: snappedCoords.x,
				y: snappedCoords.y,
				label: '',
				color: DEFAULT_PLAYER_COLOR,
			}

			// Add player and get server-assigned ID
			const serverId = await callbacks.onPlayerAdd(newPlayer)

			// Auto-link to nearby drawing control point if one exists
			const drawingTarget = findDrawingSnapTarget(snappedCoords, drawings, PLAYER_RADIUS_FEET)
			if (drawingTarget) {
				onLinkDrawingToPlayer(
					drawingTarget.drawingId,
					drawingTarget.pointId,
					serverId,
					newPlayer,
				)
			}

			// Return player with server ID
			return { ...newPlayer, id: serverId }
		},
		[coordSystem, zoom, panX, panY, callbacks, drawings, onLinkDrawingToPlayer],
	)

	const handlePlayerPositionChange = useCallback(
		(id: string, newX: number, newY: number) => {
			const player = players.find((p) => p.id === id)
			if (!player) return

			// Call before state change hook for history
			onBeforeStateChange?.()

			// Intercept callback for lineman group movement
			if (onPlayerMoveIntercept) {
				const shouldContinue = onPlayerMoveIntercept(id, newX, newY, player)
				if (shouldContinue === false) return
			}

			if (player.isLineman) {
				// Calculate offset from original position
				const offsetX = newX - player.x
				const offsetY = newY - player.y

				// Move all linemen by same offset
				const updatedPlayers = players.map((p) => {
					if (!p.isLineman) return p
					return { ...p, x: p.x + offsetX, y: p.y + offsetY }
				})
				callbacks.onPlayersChange(updatedPlayers)
			} else {
				const deltaX = newX - player.x
				const deltaY = newY - player.y

				// Normal single-player update
				callbacks.onPlayerMove(id, newX, newY)

				// Move any linked drawings by the same delta
				const linkedDrawings = drawings.filter((d) => d.playerId === id)
				if (linkedDrawings.length > 0) {
					const updatedDrawings = drawings.map((drawing) => {
						if (drawing.playerId !== id) return drawing
						// Update all points in the shared pool
						const updatedPoints: Record<string, ControlPoint> = {}
						for (const [pointId, point] of Object.entries(drawing.points)) {
							updatedPoints[pointId] = {
								...point,
								x: point.x + deltaX,
								y: point.y + deltaY,
							}
						}
						return {
							...drawing,
							points: updatedPoints,
						}
					})
					callbacks.onDrawingsChange(updatedDrawings)
				}
			}
		},
		[players, onBeforeStateChange, onPlayerMoveIntercept, callbacks, drawings],
	)

	const handlePlayerLabelClick = useCallback(
		(id: string, x: number, y: number): { id: string; x: number; y: number } | null => {
			if (drawingState.tool === TOOL_SELECT) {
				return { id, x, y }
			}
			return null
		},
		[drawingState.tool],
	)

	const handlePlayerDelete = useCallback(
		(id: string) => {
			const player = players.find((p) => p.id === id)
			if (player?.isLineman) return // Protected from deletion

			callbacks.onPlayerDelete(id)
		},
		[players, callbacks],
	)

	const handleFillPlayer = useCallback(
		(id: string) => {
			if (drawingState.tool === TOOL_FILL) {
				onBeforeStateChange?.()
				callbacks.onPlayerFill(id, drawingState.color)
			}
		},
		[drawingState.tool, drawingState.color, onBeforeStateChange, callbacks],
	)

	return {
		handleCanvasClickForPlayer,
		handlePlayerPositionChange,
		handlePlayerLabelClick,
		handlePlayerDelete,
		handleFillPlayer,
		isPlayerInteractable,
	}
}
